// packages
import { forwardRef, useEffect, useRef, useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import { ptBR } from 'date-fns/locale'
import { parse, format, isValid } from 'date-fns'
import { Calendar as CalendarIcon, X as XIcon } from 'lucide-react'

// libs
import { cn } from '@/lib/utils'

// styles
import '@/styles/components/fields/date-picker.css'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('pt-BR', ptBR)

/**
 * Componente de data alinhado ao design system do projeto.
 *
 * Convenções:
 * - O **tráfego de valores** (`value`/`onChange`) usa **ISO `yyyy-MM-dd`**.
 * - A **UI mostra e aceita** entrada em **pt-BR `dd/MM/yyyy`** com máscara.
 * - O **calendário** abre **somente** quando o usuário clica no ícone de
 *   calendário, nunca ao focar no input — assim a digitação manual flui sem
 *   o calendário roubar foco.
 *
 * Modes:
 * - `mode="single"` (default): `value: string`, `onChange(val?: string)`
 * - `mode="range"`: `value: { from?: string; to?: string }`,
 *   `onChange(val: { from?: string; to?: string })`
 */

type CommonProps = {
  className?: string
  placeholder?: string
  /** Limites em ISO yyyy-MM-dd. */
  minDate?: string
  maxDate?: string
  disabled?: boolean
  size?: 'default' | 'sm'
  clearable?: boolean
  'aria-label'?: string
  id?: string
}

export type DatePickerSingleProps = CommonProps & {
  mode?: 'single'
  value?: string
  onChange: (val?: string) => void
}

export type DatePickerRangeValue = { from?: string; to?: string }
export type DatePickerRangeProps = CommonProps & {
  mode: 'range'
  value?: DatePickerRangeValue
  onChange: (val: DatePickerRangeValue) => void
}

export type DatePickerProps = DatePickerSingleProps | DatePickerRangeProps

// ── helpers ────────────────────────────────────────────────────────────
const isoToDate = (iso?: string): Date | null => {
  if (!iso) return null
  const d = parse(iso, 'yyyy-MM-dd', new Date())
  return isValid(d) ? d : null
}
const dateToIso = (d?: Date | null): string | undefined => {
  if (!d || !isValid(d)) return undefined
  return format(d, 'yyyy-MM-dd')
}
const isoToText = (iso?: string): string => {
  const d = isoToDate(iso)
  return d ? format(d, 'dd/MM/yyyy') : ''
}
function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length >= 5) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return digits
}
function withinBounds(d: Date, minIso?: string, maxIso?: string): boolean {
  if (minIso) {
    const m = isoToDate(minIso)
    if (m && d < m) return false
  }
  if (maxIso) {
    const m = isoToDate(maxIso)
    if (m && d > m) return false
  }
  return true
}

// ── trigger de range (Button com texto formatado) ──────────────────────
interface TriggerProps {
  value?: string
  onClick?: () => void
  onClear?: () => void
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  size?: 'default' | 'sm'
  ariaLabel?: string
  id?: string
}
const RangeTrigger = forwardRef<HTMLButtonElement, TriggerProps>(function RangeTrigger(
  { value, onClick, onClear, placeholder, disabled, clearable, size = 'default', ariaLabel, id },
  ref
) {
  const h = size === 'sm' ? 'h-9' : 'h-10'
  return (
    <button
      ref={ref}
      type="button"
      id={id}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative flex items-center w-full rounded-md border border-input bg-card text-sm text-foreground',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
        'hover:border-primary/60 disabled:cursor-not-allowed disabled:opacity-50',
        'px-3 gap-2',
        h
      )}
    >
      <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className={cn('truncate text-left flex-1', !value && 'text-muted-foreground')}>{value || placeholder || 'Selecione'}</span>
      {clearable && value && !disabled && (
        <span
          role="button"
          tabIndex={-1}
          onClick={e => {
            e.stopPropagation()
            onClear?.()
          }}
          className="inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
          aria-label="Limpar data"
        >
          <XIcon className="h-3.5 w-3.5" />
        </span>
      )}
    </button>
  )
})

// ── Single mode com input mascarado + calendário sob demanda ───────────
function SingleDatePicker(props: DatePickerSingleProps) {
  const { value, onChange, placeholder, minDate, maxDate, disabled, size = 'default', clearable = true, id, className } = props
  const ariaLabel = props['aria-label']

  const [text, setText] = useState<string>(() => isoToText(value))
  const [hasError, setHasError] = useState(false)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastEmittedRef = useRef<string | undefined>(value)

  // Sincroniza texto quando o valor é alterado externamente (não pelo próprio input)
  useEffect(() => {
    if (value === lastEmittedRef.current) return
    lastEmittedRef.current = value
    setText(isoToText(value))
    setHasError(false)
  }, [value])

  // Click-outside para fechar o calendário
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const emit = (next: string | undefined) => {
    lastEmittedRef.current = next
    onChange(next)
  }

  const handleTextChange = (raw: string) => {
    const masked = applyMask(raw)
    setText(masked)
    if (masked.length === 0) {
      setHasError(false)
      emit(undefined)
      return
    }
    if (masked.length === 10) {
      const d = parse(masked, 'dd/MM/yyyy', new Date())
      const okYear = isValid(d) && d.getFullYear() >= 1900 && d.getFullYear() <= 2200
      const valid = okYear && withinBounds(d, minDate, maxDate)
      if (valid) {
        setHasError(false)
        emit(format(d, 'yyyy-MM-dd'))
      } else {
        setHasError(true)
        emit(undefined)
      }
    } else {
      // ainda incompleto — não emite, sem erro visual
      setHasError(false)
      emit(undefined)
    }
  }

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setText('')
    setHasError(false)
    emit(undefined)
  }

  const h = size === 'sm' ? 'h-9' : 'h-10'

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div
        className={cn(
          'flex items-center w-full rounded-md border bg-card text-sm transition-colors',
          'focus-within:ring-2 focus-within:ring-primary/40 focus-within:ring-offset-1',
          hasError ? 'border-red-500 focus-within:ring-red-300' : 'border-input hover:border-primary/60',
          disabled && 'opacity-50 pointer-events-none',
          'pl-2 pr-2 gap-1.5',
          h
        )}
      >
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          disabled={disabled}
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-md shrink-0',
            'text-muted-foreground hover:text-coentro hover:bg-primary/10',
            open && 'text-coentro bg-primary/10'
          )}
          aria-label="Abrir calendário"
          aria-expanded={open}
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          placeholder={placeholder || 'dd/mm/aaaa'}
          maxLength={10}
          onChange={e => handleTextChange(e.target.value)}
          disabled={disabled}
          aria-invalid={hasError}
          aria-label={ariaLabel}
          className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground/70 min-w-0"
        />
        {clearable && text && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
            aria-label="Limpar data"
            tabIndex={-1}
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 left-0">
          <ReactDatePicker
            inline
            selected={isoToDate(value) ?? undefined}
            onChange={(date: Date | null) => {
              const iso = dateToIso(date)
              setText(date ? format(date, 'dd/MM/yyyy') : '')
              setHasError(false)
              emit(iso)
              setOpen(false)
            }}
            minDate={isoToDate(minDate) ?? undefined}
            maxDate={isoToDate(maxDate) ?? undefined}
            locale="pt-BR"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            calendarClassName="custom-datepicker"
          />
        </div>
      )}
    </div>
  )
}

// ── Range mode (mantido com Button trigger) ────────────────────────────
function RangeDatePickerInternal(props: DatePickerRangeProps) {
  const { className, placeholder, minDate, maxDate, disabled, size = 'default', clearable = true, id } = props
  const ariaLabel = props['aria-label']
  const fromD = isoToDate(props.value?.from)
  const toD = isoToDate(props.value?.to)
  const displayValue =
    fromD && toD
      ? `${format(fromD, 'dd/MM/yyyy')} – ${format(toD, 'dd/MM/yyyy')}`
      : fromD
        ? `${format(fromD, 'dd/MM/yyyy')} – …`
        : ''

  return (
    <div className={cn('inline-block w-full', className)}>
      <ReactDatePicker
        selectsRange
        startDate={fromD ?? undefined}
        endDate={toD ?? undefined}
        onChange={(dates: [Date | null, Date | null]) => {
          const [start, end] = dates
          props.onChange({ from: dateToIso(start), to: dateToIso(end) })
        }}
        minDate={isoToDate(minDate) ?? undefined}
        maxDate={isoToDate(maxDate) ?? undefined}
        locale="pt-BR"
        dateFormat="dd/MM/yyyy"
        showPopperArrow={false}
        popperPlacement="bottom-start"
        calendarClassName="custom-datepicker"
        disabled={disabled}
        customInput={
          <RangeTrigger
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            clearable={clearable}
            size={size}
            ariaLabel={ariaLabel}
            id={id}
            onClear={() => props.onChange({ from: undefined, to: undefined })}
          />
        }
      />
    </div>
  )
}

// ── public ─────────────────────────────────────────────────────────────
export function DatePicker(props: DatePickerProps) {
  if (props.mode === 'range') return <RangeDatePickerInternal {...props} />
  return <SingleDatePicker {...(props as DatePickerSingleProps)} />
}

export default DatePicker
